// ======================================================================
// File: PurchaseInvoiceLineGrid.jsx
// Module: PPE → Procurement → Purchase Invoice
//
// Purpose:
// - High volume invoice line workspace
// - Supports 500+ / 1000+ / 5000+ lines
// - Search
// - Pagination
// - Editable grid in DRAFT
// - Available Qty validation
// - Real-time line calculations
//
// Editable Fields:
//
// DRAFT:
//   invoice_qty
//   unit_price
//   tax_percent
//   remarks
//
// All Other Status:
//   Read Only
// ======================================================================

import React from "react";

const PAGE_SIZES = [50, 100, 250, 500];

const PurchaseInvoiceLineGrid = ({
  lines = [],
  allLines = [],
  setLines,
  isDraft,
  setDirty,

  searchText,
  setSearchText,

  pageSize,
  setPageSize,

  currentPage,
  setCurrentPage,

  totalRecords,
  totalPages
}) => {

  // ============================================================
  // Update Line
  // ============================================================

  const updateLine = (
    rowIndex,
    field,
    value
  ) => {

    if (!isDraft) return;

    const updatedLines = [...allLines];

    const actualIndex =
      (currentPage - 1) * pageSize + rowIndex;

    const line = {
      ...updatedLines[actualIndex]
    };

    line[field] = value;

    // ========================================================
    // Validation
    // ========================================================

    const invoiceQty =
      Number(
        field === "invoice_qty"
          ? value
          : line.invoice_qty || 0
      );

    const availableQty =
      Number(
        line.available_qty ??
        line.grn_qty ??
        0
      );

    if (invoiceQty > availableQty) {
      line.validation_error =
        "Invoice Qty cannot exceed Available Qty";
    } else {
      line.validation_error = "";
    }

    // ========================================================
    // Recalculate
    // ========================================================

    const unitPrice =
      Number(line.unit_price || 0);

    const taxPercent =
      Number(line.tax_percent || 0);

    const materialValue =
      invoiceQty * unitPrice;

    const taxAmount =
      (materialValue * taxPercent) / 100;

    const lineTotal =
      materialValue + taxAmount;

    line.material_value = materialValue;
    line.tax_amount = taxAmount;
    line.line_total = lineTotal;

    updatedLines[actualIndex] = line;

    setLines(updatedLines);

    setDirty(true);
  };

  // ============================================================
  // Pagination
  // ============================================================

  const renderPagination = () => {

    if (totalPages <= 1) return null;

    return (
      <div className="pi-pagination">

        <button
          disabled={currentPage === 1}
          onClick={() =>
            setCurrentPage(
              currentPage - 1
            )
          }
        >
          Previous
        </button>

        <span>
          Page {currentPage} of {totalPages}
        </span>

        <button
          disabled={
            currentPage === totalPages
          }
          onClick={() =>
            setCurrentPage(
              currentPage + 1
            )
          }
        >
          Next
        </button>

      </div>
    );
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="pi-card">

      {/* ===================================================== */}
      {/* Header                                                */}
      {/* ===================================================== */}

      <div className="pi-card-title">
        Invoice Lines
      </div>

      {/* ===================================================== */}
      {/* Toolbar                                               */}
      {/* ===================================================== */}

      <div className="pi-grid-toolbar">

        <input
          type="text"
          placeholder="Search material..."
          value={searchText}
          onChange={(e) =>
            setSearchText(e.target.value)
          }
        />

        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(
              Number(e.target.value)
            );

            setCurrentPage(1);
          }}
        >
          {PAGE_SIZES.map((size) => (
            <option
              key={size}
              value={size}
            >
              {size}
            </option>
          ))}
        </select>

        <div className="pi-record-count">
          Showing{" "}
          {totalRecords === 0
            ? 0
            : (currentPage - 1) *
                pageSize +
              1}
          {" - "}
          {Math.min(
            currentPage * pageSize,
            totalRecords
          )}
          {" of "}
          {totalRecords}
        </div>

      </div>

      {/* ===================================================== */}
      {/* Table                                                 */}
      {/* ===================================================== */}

      <div className="pi-grid-wrapper">

        <table className="pi-line-table">

          <thead>

            <tr>

              <th>Material Code</th>

              <th>Material Name</th>

              <th>Material Type</th>

              <th>GRN Qty</th>

              <th>Previously Invoiced</th>

              <th>Available Qty</th>

              <th>Invoice Qty</th>

              <th>UOM</th>

              <th>Rate</th>

              <th>GST %</th>

              <th>GST Amount</th>

              <th>Line Total</th>

              <th>Remarks</th>

            </tr>

          </thead>

          <tbody>

            {lines.length === 0 && (
              <tr>
                <td
                  colSpan="13"
                  className="pi-no-data"
                >
                  No invoice lines found
                </td>
              </tr>
            )}

            {lines.map((line, index) => (

              <React.Fragment
                key={
                  line.id ||
                  line.grn_line_id ||
                  index
                }
              >

                <tr>

                  <td>
                    {line.material_code}
                  </td>

                  <td>
                    {line.material_name}
                  </td>

                  <td>
                    {line.material_type}
                  </td>

                  <td>
                    {line.grn_qty}
                  </td>

                  <td>
                    {
                      line.previously_invoiced_qty
                    }
                  </td>

                  <td>
                    {line.available_qty}
                  </td>

                  <td>

                    <input
                      type="number"
                      value={
                        line.invoice_qty || ""
                      }
                      disabled={!isDraft}
                      onChange={(e) =>
                        updateLine(
                          index,
                          "invoice_qty",
                          e.target.value
                        )
                      }
                    />

                  </td>

                  <td>
                    {line.uom}
                  </td>

                  <td>

                    <input
                      type="number"
                      step="0.01"
                      value={
                        line.unit_price || ""
                      }
                      disabled={!isDraft}
                      onChange={(e) =>
                        updateLine(
                          index,
                          "unit_price",
                          e.target.value
                        )
                      }
                    />

                  </td>

                  <td>

                    <input
                      type="number"
                      step="0.01"
                      value={
                        line.tax_percent || ""
                      }
                      disabled={!isDraft}
                      onChange={(e) =>
                        updateLine(
                          index,
                          "tax_percent",
                          e.target.value
                        )
                      }
                    />

                  </td>

                  <td>
                    {Number(
                      line.tax_value ||
                      line.tax_amount ||
                      0
                    ).toFixed(2)}
                  </td>

                  <td>
                    {Number(
                      line.net_value ||
                      line.line_total ||
                      0
                    ).toFixed(2)}
                  </td>

                  <td>

                    <input
                      type="text"
                      value={
                        line.remarks || ""
                      }
                      disabled={!isDraft}
                      onChange={(e) =>
                        updateLine(
                          index,
                          "remarks",
                          e.target.value
                        )
                      }
                    />

                  </td>

                </tr>

                {line.validation_error && (

                  <tr>

                    <td
                      colSpan="13"
                      className="pi-line-error"
                    >
                      {
                        line.validation_error
                      }
                    </td>

                  </tr>

                )}

              </React.Fragment>

            ))}

          </tbody>

        </table>

      </div>

      {/* ===================================================== */}
      {/* Pagination                                            */}
      {/* ===================================================== */}

      {renderPagination()}

    </div>
  );
};

export default PurchaseInvoiceLineGrid;